import { Component, input, signal, output, computed, inject, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { ModalController } from '@ionic/angular/standalone';
import { MultiselectModalComponent } from './multiselect-modal.component';
import { IconComponent } from '@net3-multiplatform-client/libs';

export interface SelectOptionMultiple {
  value: string;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'lib-multiselect',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './multiselect.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
  ],
})
export class MultiSelectComponent implements ControlValueAccessor {

  options = input<SelectOptionMultiple[]>([]);
  placeholder = input('Selecciona...');
  className = input('');
  title = input('Selecciona');
  selectAllLabel = input('Seleccionar todos');
  deselectAllLabel = input('Deseleccionar todos');

  multipleValueChange = output<string[]>();

  private readonly modalCtrl = inject(ModalController);
  private readonly selectedValues = signal<string[]>([]);
  private onChange: (val: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  isDisabled = signal(false);

  multipleText = computed(() => {
    const selected = this.selectedValues();
    if (selected.length === 0) return null;
    if (selected.length === 1) {
      return this.options().find(o => o.value === selected[0])?.label ?? '1 seleccionado';
    }
    return `${selected.length} seleccionados`;
  });

  async openModal(): Promise<void> {
    if (this.isDisabled()) return;
    this.onTouched();

    const modal = await this.modalCtrl.create({
      component: MultiselectModalComponent,
      componentProps: {
        options: this.options(),
        selected: this.selectedValues(),
        title: this.title(),
        selectAllLabel: this.selectAllLabel(),
        deselectAllLabel: this.deselectAllLabel(),
      },
      showBackdrop: true,
      cssClass: 'multiselect-modal',
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm' && data) {
      const prev = this.selectedValues();
      const hasChanged =
        data.length !== prev.length ||
        data.some((v: string) => !prev.includes(v));

      if (hasChanged) {
        this.selectedValues.set(data);
        this.multipleValueChange.emit(data);
        this.onChange(data);
      }
    }
  }

  // ControlValueAccessor
  writeValue(val: string[] | null): void {
    this.selectedValues.set(val ?? []);
  }

  registerOnChange(fn: (val: string[]) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { 
    this.isDisabled.set(isDisabled); 
  }
}